const MONTHS_PER_YEAR = 12;
const WEEKS_PER_YEAR = 52;
export const TIME_SAVINGS_MINUTES_SAVED_PER_TASK = 30;
const MINUTES_WITHOUT_PALEN_PER_TASK = 35;
const MINUTES_WITH_PALEN_PER_TASK = 5;
const HOURS_PER_WORK_DAY = 8;
const WORK_DAYS_PER_MONTH = 20;

export interface TimeSavingsInputs {
  monthlyTransports: number;
  weeklySearches: number;
}

export const TIME_SAVINGS_DEFAULT_INPUTS = {
  monthlyTransports: 10,
  weeklySearches: 5,
} as const satisfies TimeSavingsInputs;

export interface TimeSavingsEquivalents {
  workDays: number;
  workMonths: number;
}

export interface TimeSavingsDisplay {
  unit: 'hours' | 'workDays' | 'workMonths';
  value: number;
}

export interface AnnualTimeSavingsBreakdown {
  searchHours: number;
  transportHours: number;
}

export interface DefaultTimeSavingsFacts {
  annualHours: number;
  display: TimeSavingsDisplay;
  inputs: Readonly<TimeSavingsInputs>;
  minutesSavedPerTask: number;
}

export interface CumulativeWorkloadComparison {
  withPalenHours: number[];
  withoutPalenHours: number[];
}

export function normalizeTimeSavingsInput(value: number): number {
  if (!Number.isFinite(value) || value < 0) return 0;
  return Math.min(Math.round(value), 10_000);
}

export function calculateMonthlySavedHours({
  monthlyTransports,
  weeklySearches,
}: TimeSavingsInputs): number {
  const { searchHours, transportHours } = calculateAnnualTimeSavingsBreakdown({ monthlyTransports, weeklySearches });

  return (searchHours + transportHours) / MONTHS_PER_YEAR;
}

export function calculateAnnualTimeSavingsBreakdown({
  monthlyTransports,
  weeklySearches,
}: TimeSavingsInputs): AnnualTimeSavingsBreakdown {
  const hoursPerTask = TIME_SAVINGS_MINUTES_SAVED_PER_TASK / 60;

  return {
    searchHours: normalizeTimeSavingsInput(weeklySearches) * WEEKS_PER_YEAR * hoursPerTask,
    transportHours: normalizeTimeSavingsInput(monthlyTransports) * MONTHS_PER_YEAR * hoursPerTask,
  };
}

export function buildCumulativeSavingsCurve(inputs: TimeSavingsInputs): number[] {
  const monthlyHours = calculateMonthlySavedHours(inputs);
  return Array.from({ length: MONTHS_PER_YEAR + 1 }, (_, month) => monthlyHours * month);
}

export function buildCumulativeWorkloadComparison({
  monthlyTransports,
  weeklySearches,
}: TimeSavingsInputs): CumulativeWorkloadComparison {
  const annualTasks = (
    normalizeTimeSavingsInput(weeklySearches) * WEEKS_PER_YEAR
    + normalizeTimeSavingsInput(monthlyTransports) * MONTHS_PER_YEAR
  );
  const monthlyTasks = annualTasks / MONTHS_PER_YEAR;

  return {
    withPalenHours: Array.from(
      { length: MONTHS_PER_YEAR + 1 },
      (_, month) => monthlyTasks * month * MINUTES_WITH_PALEN_PER_TASK / 60,
    ),
    withoutPalenHours: Array.from(
      { length: MONTHS_PER_YEAR + 1 },
      (_, month) => monthlyTasks * month * MINUTES_WITHOUT_PALEN_PER_TASK / 60,
    ),
  };
}

export function calculateTimeSavingsEquivalents(hours: number): TimeSavingsEquivalents {
  const normalizedHours = Number.isFinite(hours) && hours > 0 ? hours : 0;
  const workDays = normalizedHours / HOURS_PER_WORK_DAY;

  return {
    workDays,
    workMonths: workDays / WORK_DAYS_PER_MONTH,
  };
}

export function calculateTimeSavingsDisplay(hours: number): TimeSavingsDisplay {
  const normalizedHours = Number.isFinite(hours) && hours > 0 ? hours : 0;
  const { workDays, workMonths } = calculateTimeSavingsEquivalents(normalizedHours);

  if (workDays >= 25) return { unit: 'workMonths', value: workMonths };
  if (workDays >= 0.8) return { unit: 'workDays', value: workDays };
  return { unit: 'hours', value: normalizedHours };
}

export function buildDefaultTimeSavingsFacts(): DefaultTimeSavingsFacts {
  const { searchHours, transportHours } = calculateAnnualTimeSavingsBreakdown(TIME_SAVINGS_DEFAULT_INPUTS);
  const annualHours = searchHours + transportHours;

  return {
    annualHours,
    display: calculateTimeSavingsDisplay(annualHours),
    inputs: TIME_SAVINGS_DEFAULT_INPUTS,
    minutesSavedPerTask: TIME_SAVINGS_MINUTES_SAVED_PER_TASK,
  };
}
